export class AuthRefreshTransientError extends Error {
  constructor(message = 'The authentication session could not be refreshed safely.', cause) {
    super(message);
    this.name = 'AuthRefreshTransientError';
    this.cause = cause;
    this.terminal = false;
  }
}

const createTerminalError = () => {
  const error = new Error('The refresh credential was definitively rejected.');
  error.name = 'AuthRefreshTerminalError';
  error.terminal = true;
  return error;
};

const validTokens = tokens =>
  tokens && typeof tokens.access === 'string' && tokens.access && typeof tokens.refresh === 'string' && tokens.refresh;

// Verified against djangorestframework-simplejwt 5.3.0 in the PocketSafe
// backend: malformed, expired, revoked, and blacklisted refresh credentials are
// normalized by TokenViewBase to this exact status + stable response code.
export const isSimpleJwtTerminalRefreshFailure = error =>
  error?.response?.status === 401 && error?.response?.data?.code === 'token_not_valid';

/**
 * Coordinates rotating refresh credentials in one tab and, through the injected
 * exclusive lock, between tabs. Coordination state contains metadata only.
 * Token values are read and written exclusively through authStorage.
 */
export const createAuthRefreshCoordinator = ({
  authStorage,
  coordinationStorage,
  isTerminalRefreshFailure,
  leaseMs,
  lock,
  now,
  ownerId,
  publish,
  redirectToLogin,
  refreshRequest,
  setAuthorization,
}) => {
  let inFlightRefresh = null;
  let redirected = false;
  let lastObservedVersion = coordinationStorage.read().version || 0;

  const redirectOnce = () => {
    if (redirected) return;
    redirected = true;
    redirectToLogin();
  };

  const applyTerminalLocally = ({ clearSharedStorage }) => {
    if (clearSharedStorage) authStorage.clear();
    setAuthorization(null);
    redirectOnce();
  };

  const isCurrentOwner = ownedState => {
    const currentState = coordinationStorage.read();
    return (
      currentState.ownerId === ownerId &&
      currentState.fence === ownedState.fence &&
      currentState.leaseExpiresAt === ownedState.leaseExpiresAt &&
      now() <= ownedState.leaseExpiresAt
    );
  };

  const requireCurrentOwner = ownedState => {
    if (!isCurrentOwner(ownedState)) {
      throw new AuthRefreshTransientError('Refresh ownership expired or was superseded before credentials could be stored.');
    }
  };

  const publishState = state => {
    publish({ status: state.status, version: state.version });
  };

  const finishTerminalRefresh = ownedState => {
    requireCurrentOwner(ownedState);
    authStorage.clear();
    setAuthorization(null);
    const clearedState = {
      ...ownedState,
      leaseExpiresAt: 0,
      ownerId: null,
      status: 'cleared',
      version: ownedState.version + 1,
    };
    coordinationStorage.write(clearedState);
    lastObservedVersion = clearedState.version;
    publishState(clearedState);
    redirectOnce();
    throw createTerminalError();
  };

  const releaseAfterTransientFailure = ownedState => {
    if (!isCurrentOwner(ownedState)) return;
    coordinationStorage.write({
      ...ownedState,
      leaseExpiresAt: 0,
      ownerId: null,
      status: 'idle',
    });
  };

  const performRefresh = async () => {
    const observedVersion = coordinationStorage.read().version || 0;

    try {
      return await lock.runExclusive(async () => {
        const currentState = coordinationStorage.read();
        const currentRefresh = authStorage.getRefresh();

        if (currentState.version > observedVersion) {
          lastObservedVersion = currentState.version;
          if (currentState.status === 'cleared' || !currentRefresh) {
            applyTerminalLocally({ clearSharedStorage: false });
            throw createTerminalError();
          }

          const currentAuthorization = setAuthorization(authStorage.getAccess());
          if (currentAuthorization) return currentAuthorization;
        }

        if (currentState.status === 'cleared' && !currentRefresh) {
          applyTerminalLocally({ clearSharedStorage: false });
          throw createTerminalError();
        }

        const ownedState = {
          ...currentState,
          fence: (currentState.fence || 0) + 1,
          leaseExpiresAt: now() + leaseMs,
          ownerId,
          status: 'refreshing',
        };
        coordinationStorage.write(ownedState);

        if (!currentRefresh) return finishTerminalRefresh(ownedState);

        let tokens;
        try {
          tokens = await refreshRequest(currentRefresh);
        } catch (error) {
          if (isTerminalRefreshFailure(error)) return finishTerminalRefresh(ownedState);
          releaseAfterTransientFailure(ownedState);
          throw new AuthRefreshTransientError('Refresh failed without a definitive credential rejection.', error);
        }

        if (!validTokens(tokens)) {
          releaseAfterTransientFailure(ownedState);
          throw new AuthRefreshTransientError('Refresh returned an invalid success payload.');
        }

        requireCurrentOwner(ownedState);
        authStorage.store({ access: tokens.access, refresh: tokens.refresh });
        const authorization = setAuthorization(tokens.access);
        const updatedState = {
          ...ownedState,
          leaseExpiresAt: 0,
          ownerId: null,
          status: 'updated',
          version: ownedState.version + 1,
        };
        coordinationStorage.write(updatedState);
        lastObservedVersion = updatedState.version;
        publishState(updatedState);
        return authorization;
      });
    } catch (error) {
      if (error?.terminal === true || error instanceof AuthRefreshTransientError) throw error;
      throw new AuthRefreshTransientError('The browser refresh lock could not be acquired safely.', error);
    }
  };

  const refresh = () => {
    if (!inFlightRefresh) {
      inFlightRefresh = performRefresh().finally(() => {
        inFlightRefresh = null;
      });
    }
    return inFlightRefresh;
  };

  const applyExternalSignal = signal => {
    if (!signal || !['updated', 'cleared'].includes(signal.status) || !Number.isFinite(signal.version)) return;
    if (signal.version <= lastObservedVersion) return;

    const currentState = coordinationStorage.read();
    if (currentState.version < signal.version || currentState.status !== signal.status) return;
    lastObservedVersion = currentState.version;

    if (currentState.status === 'cleared') {
      applyTerminalLocally({ clearSharedStorage: false });
      return;
    }
    setAuthorization(authStorage.getAccess());
  };

  return { applyExternalSignal, refresh };
};
